import { graphql, useStaticQuery } from "gatsby"
import gsap from "gsap"
import ScrollToPlugin from "gsap/ScrollToPlugin"
import UniversalLink from "library/Loader/UniversalLink"
import ScaledContent from "library/ScaledContent"
import { ScreenContext } from "library/ScreenContext"
import { usePinType } from "library/Scroll"
import UniversalImage from "library/UniversalImage"
import { DesktopOnly, TabletMobileOnly } from "library/breakpointUtils"
import { eases } from "library/eases"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useAnimation from "library/useAnimation"
import useMedia from "library/useMedia"
import { useResponsivePixels } from "library/viewportUtils"
import { useContext, useRef, useState } from "react"
import Pricing from "sections/06-Pricing"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"

gsap.registerPlugin(ScrollToPlugin)

export default function Steps() {
	const data: Queries.StepsQuery = useStaticQuery(graphql`
		query Steps {
			allStepsJson {
				edges {
					node {
						image {
							childImageSharp {
								gatsbyImageData(layout: FULL_WIDTH)
							}
						}
						number
						title
						description
						overflowTop
						tabletOverflowTop
						overflowBottom
						mobileOverflowLeft
						tabletDescriptionWidth
					}
				}
			}
		}
	`)

	const steps = data.allStepsJson.edges.map((edge) => edge.node)
	const graphic = useRef<HTMLDivElement | null>(null)
	const innerBar = useRef<HTMLDivElement | null>(null)
	const wrapper = useRef<HTMLDivElement | null>(null)
	const cardHeight = useResponsivePixels(useMedia(190, 190, 274, 50)) ?? 0
	const [activeCard, setActiveCard] = useState(0)
	const pinType = usePinType()
	const rightRef = useRef<HTMLDivElement | null>(null)
	const stepsHeight = useMedia(608, 608, 1500, 400)
	const { mobile, tablet } = useContext(ScreenContext)

	useAnimation(() => {
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: graphic.current,
				start: `top center-=${cardHeight / 2}`,
				end: "bottom 53.6%",
				endTrigger: wrapper.current,
				pin: innerBar.current,
				pinType,
				pinSpacing: false,
				scrub: true,

				onUpdate: (self) => {
					const card = Math.floor(self.progress * steps.length)
					setActiveCard(card)
				},
				onLeave: () => {
					setActiveCard(steps.length)
				},
				onEnterBack: () => {
					setActiveCard(steps.length - 1)
				},
			},
		})

		if (!mobile && !tablet) {
			tl.to(
				graphic.current,
				{
					y: cardHeight * 2.25,
					duration: 1,
				},
				0,
			)

			tl.fromTo(
				".active-line",
				{ drawSVG: "100% 100%" },
				{
					drawSVG: "0% 100%",
					duration: 0.1,
					ease: "power1.out",
				},
				0.9,
			)

			tl.to(
				".panel-border",
				{
					borderColor: colors.blue3,
					duration: 0.1,
					ease: "power1.out",
				},
				0.9,
			)

			tl.to(
				".panel",
				{
					backgroundColor: colors.transBlue2,
					duration: 0.1,
					ease: "power1.out",
				},
				0.9,
			)

			tl.to(
				".panel-kicker",
				{
					borderColor: colors.green3,
					backgroundColor: "rgba(46, 179, 135, 0.12)",
					duration: 0.1,
					ease: "power1.out",
				},
				0.9,
			)

			tl.to(
				".box-path",
				{
					fill: colors.green3,
					duration: 0.1,
					ease: "power1.out",
				},
				0.9,
			)

			tl.to(
				".check-path",
				{
					stroke: colors.green3,
					duration: 0.1,
					ease: "power1.out",
				},
				0.9,
			)

			tl.to(
				innerBar.current,
				{
					scaleY: 0.5,
					duration: 0.1,
					transformOrigin: "bottom",
				},
				0.9,
			)
		}
	}, [cardHeight, pinType, steps.length, mobile, tablet])

	return (
		<>
			<Wrapper ref={wrapper}>
				<Left ref={graphic}>
					{steps.map((step, index) => (
						<DesktopOnly key={step.title}>
							<Graphic
								image={step.image}
								alt=""
								$overflowTop={step.overflowTop}
								style={{ opacity: activeCard === index ? 1 : 0 }}
							/>
						</DesktopOnly>
					))}
				</Left>
				<Bar>
					<InnerBar ref={innerBar} />
				</Bar>
				<Right ref={rightRef}>
					{steps.map((step, index) => (
						<Step
							key={step.title}
							$isActive={index === activeCard}
							type="button"
							onClick={(e) => {
								// scroll this element into the center of the screen
								const el = e.currentTarget
								if (el instanceof HTMLElement)
									gsap.to(window, {
										scrollTo: {
											y: el,
											offsetY: window.innerHeight / 2 - el.offsetHeight / 2,
										},
									})
							}}
						>
							<Title>
								<StepNumber>{step.number}</StepNumber>
								<div>{step.title}</div>
							</Title>
							<Description $tabletWidth={step.tabletDescriptionWidth}>
								{step.description}
							</Description>
							<TabletMobileOnly>
								<ScaledContent scale={useMedia(1, 1, 1, 0.5)}>
									<Graphic
										image={step.image}
										alt=""
										$overflowTop={step.tabletOverflowTop}
										$overflowBottom={step.overflowBottom}
										$overflowLeft={step.mobileOverflowLeft}
										style={{ opacity: activeCard === index ? 1 : 0.5 }}
									/>
								</ScaledContent>
							</TabletMobileOnly>
						</Step>
					))}
				</Right>
			</Wrapper>
			<div>
				<Pricing />
			</div>
		</>
	)
}

const Wrapper = styled.div`
	${fresponsive(css`
		display: grid;
		grid-template-columns: auto 1fr auto;
		padding: 155px 154px 168px 78px;
		gap: 67px;
		width: 100%;
	`)}

	${ftablet(css`
		gap: 0;
		padding: 0;
		padding-bottom: 112px;
	`)}

  ${fmobile(css`
		padding: 60px 0 0;
		grid-template-columns: unset;
	`)}
`

const Bar = styled.div`
	height: 100%;
	background: ${colors.grey1};
	width: 1px;

	${fresponsive(css`
		height: calc(100% + 168px);
	`)}

	${ftablet(css`
		display: none;
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const InnerBar = styled.div`
	${fresponsive(css`
		height: 130px;
		width: 100%;
		background: ${colors.blue3};
		position: relative;
		z-index: 2;
	`)}

	${ftablet(css`
		height: 274px;
	`)}
`

const Right = styled.div`
	${fresponsive(css`
		display: grid;
		gap: 122px;
		width: 470px;
	`)}

	${ftablet(css`
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 79px 177px 0;
		gap: 80px;
	`)}

	${fmobile(css`
		width: 100%;
		gap: 40px;
	`)}
`

const Step = styled(UniversalLink)<{
	$isActive: boolean
}>`
	${({ $isActive }) =>
		fresponsive(css`
			width: 456px;

			& ${Title} {
				color: ${$isActive ? colors.grey4 : colors.grey2};

				& ${StepNumber} {
					background: ${$isActive ? colors.transBlue1 : colors.alpine};
					border: 1px solid ${$isActive ? colors.blue3 : colors.grey2};
					color: ${$isActive ? colors.blue3 : colors.grey3};
				}
			}

			& ${Description} {
				color: ${$isActive ? colors.grey3 : colors.grey2};
			}
		`)}

	${ftablet(css`
		display: flex;
		flex-direction: column;
		align-items: center;
	`)}

	${fmobile(css`
		width: 100%;
	`)}

	&:last-of-type {
		${ftablet(css`
			margin-top: 20px;
		`)}
	}
`

const Title = styled.div`
	${fresponsive(css`
		transition: all 0.1s ${eases.quad.inOut};
		${textStyles.h5};
		display: flex;
		align-items: center;
		gap: 11px;
		margin-bottom: 20px;
	`)}

	${ftablet(css`
		transition: all 0.5s ${eases.quad.inOut};
		flex-direction: column;
		gap: 24px;
		${textStyles.h4};
		white-space: nowrap;
		margin-bottom: 0;
	`)}

  ${fmobile(css`
		transition: all 0.5s ${eases.quad.inOut};
		${textStyles.h6};
	`)}
`

const StepNumber = styled.div`
	${fresponsive(css`
		transition: all 0.1s ${eases.quad.inOut};
		${textStyles.sub3}
		width: 40px;
		height: 40px;
		display: grid;
		place-items: center;
		color: ${colors.blue3};
		border: 1px solid ${colors.blue3};
		background: ${colors.transBlue1};
		border-radius: 8px;
		flex-shrink: 0;
	`)}

	${ftablet(css`
		transition: all 0.5s ${eases.quad.inOut};
	`)}

	${fmobile(css`
		transition: all 0.5s ${eases.quad.inOut};
	`)}
`

const Description = styled.div<{
	$tabletWidth?: string | null
}>`
	${fresponsive(css`
		transition: all 0.1s ${eases.quad.inOut};
		${textStyles.p2}
		padding-left: 50px;
		text-align: left;
	`)}

	${({ $tabletWidth }) =>
		ftablet(css`
			transition: all 0.5s ${eases.quad.inOut};
			width: ${$tabletWidth};
			${textStyles.p2};
			text-align: center;
			padding-left: 0;
			margin-top: 19px;
		`)}

  ${fmobile(css`
		transition: all 0.5s ${eases.quad.inOut};
		${textStyles.p3};
		width: 279px;
		padding-left: 0;
	`)}
`

const Graphic = styled(UniversalImage)<{
	$overflowTop?: string | null
	$overflowLeft?: string | null
	$overflowBottom?: string | null
}>`
	${({ $overflowLeft, $overflowTop, $overflowBottom }) =>
		fresponsive(css`
			width: ${$overflowLeft ? `calc(613px + ${$overflowLeft})` : "613px"};
			margin-left: ${$overflowLeft ? `-${$overflowLeft}` : "-38px"};
			margin-top: ${$overflowTop ? `-${$overflowTop}` : "56px"};
			margin-bottom: ${$overflowBottom ? `-${$overflowBottom}` : "0"};
			transition: opacity 0.3s ${eases.quad.inOut};
		`)}

	${({ $overflowTop }) =>
		ftablet(css`
			margin-top: ${$overflowTop ? $overflowTop : "56px"};
			transition: opacity 0.5s ${eases.quad.inOut};
		`)}

	${({ $overflowLeft }) =>
		fmobile(css`
			margin-left: ${$overflowLeft ? `-${$overflowLeft}` : "0"};
			transition: opacity 0.5s ${eases.quad.inOut};
		`)}
`

const Left = styled.div`
	display: grid;
	align-items: start;
	align-self: start;

	& > * {
		grid-area: 1 / 1 / 2 / 2;
	}

	${fresponsive(css`
		/* for centering, is (height of inner bar - height of card) / 2 */
		padding-top: 17px;
	`)}

	${fmobile(css`
		display: none;
	`)}
`
